import { useCallback, useState } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import {
  ConfirmDialog,
  ConfirmDialogProps,
} from 'src/core/interim/dialogs/ConfirmDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { Feature, SaleClient, SaleDetails } from 'src/WebApiController';

export type TurnOnSaleAutoFulfillDialogProps = Omit<
  ConfirmDialogProps,
  'headerText' | 'bodyText'
> & {
  saleId: number;
};

export const TurnOnSaleAutoFulfillDialog = ({
  saleId,
  onOkay,
  onCancel,
  ...props
}: TurnOnSaleAutoFulfillDialogProps) => {
  const { showErrorDialog } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const hasConsigneeSingleEntityUpdateFeature = useUserHasFeature(
    Feature.ConsigneeSingleEntityUpdate
  );

  const { posEntity: sale, setActivePosEntity } =
    useActivePosEntityContext<SaleDetails>();

  const onTurnOnAutoFulfill = useCallback(async () => {
    setIsLoading(true);
    await tryInvokeApi(
      async () => {
        if (hasConsigneeSingleEntityUpdateFeature) {
          await new SaleClient(
            activeAccountWebClientConfig
          ).updateSaleIsNoFulfillBySaleId(saleId, {
            item1: saleId,
            item2: false,
          });
        } else {
          await new SaleClient(
            activeAccountWebClientConfig
          ).updateSaleIsNoFulfill({
            item1: saleId,
            item2: false,
          });
        }

        setIsLoading(false);

        if (sale) {
          setActivePosEntity(sale.id, sale.idOnMkp, true);
        }

        onOkay();
      },
      (error) => {
        showErrorDialog('SaleClient.updateSaleIsNoFulfill', error, {
          trackErrorData: {
            saleId,
          },
        });
      },
      () => setIsLoading(false)
    );
  }, [
    activeAccountWebClientConfig,
    hasConsigneeSingleEntityUpdateFeature,
    onOkay,
    sale,
    saleId,
    setActivePosEntity,
    showErrorDialog,
  ]);

  return (
    <ConfirmDialog
      headerText={<Content id={ContentId.AutoFulfill} />}
      bodyText={<Content id={ContentId.TurnOnAutoFulfillPrompt} />}
      onOkay={onTurnOnAutoFulfill}
      onCancel={onCancel}
      okText={ContentId.Yes}
      cancelText={ContentId.No}
      {...props}
      isLoading={isLoading}
    />
  );
};
